import { useState } from "react"
import { ArrowRightLeft, FileText, Lock, LockOpen, Smartphone, Tags, Truck, X } from "lucide-react"
import type { AppShell } from "@/App"
import { downloadDocument, driverLink, errorMessage, moveOrder, pinOrder, recordResult, type Order, type PlanRoute, type PlanView, type Vehicle, type Warehouse } from "@/lib/api"
import { day10, dayShort, dm, eur, hhmm, n } from "@/lib/format"
import { AsyncButton, Note, Tag } from "@/components/kit"
import { LoadCell } from "@/pages/Orders"
import { MoveDialog } from "@/pages/MoveDialog"
import { RouteMap } from "@/pages/RouteMap"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/**
 * Rota aberta (veículo × dia): mapa, paragens por ordem, documentos, link do
 * motorista (dia confirmado) e resultados. Usada no quadro da frota e em
 * «Planear o dia»; `actions` acrescenta botões (ex.: «Sugerir ordem»).
 */
export function RoutePanel({
  shell,
  view,
  route,
  vehicles,
  warehouse,
  orders: byOrder,
  canEdit,
  onReload,
  actions,
}: {
  shell: AppShell
  view: PlanView
  route: PlanRoute
  vehicles: Vehicle[]
  warehouse: Warehouse | null
  orders: Map<string, Order>
  canEdit: boolean
  onReload: () => Promise<void>
  actions?: React.ReactNode
}) {
  const [moving, setMoving] = useState<Order | null>(null)
  const [openStop, setOpenStop] = useState<string | null>(null)
  const [failReason, setFailReason] = useState("")
  // F2 — link do motorista da rota selecionada (URL do portal com o token no fragmento).
  const [driverUrl, setDriverUrl] = useState<{ route_id: string; url: string | null; token: string; copied: boolean } | null>(null)
  const routeStops = view.stops.filter((s) => s.route_id === route.id).sort((a, b) => a.seq - b.seq)

  const shareDriverLink = async (routeId: string) => {
    const link = await driverLink(shell, routeId)
    const url = link.url ?? `${window.location.origin}/portal/#${link.token}`
    let copied = false
    try {
      await navigator.clipboard.writeText(url)
      copied = true
    } catch {
      copied = false
    }
    setDriverUrl({ route_id: routeId, url, token: link.token, copied })
    shell.toast(copied ? "success" : "info", copied ? "Link do motorista copiado" : "Link do motorista criado", copied ? "Cola-o numa mensagem para o motorista." : "Copia-o do campo abaixo.")
  }

  const run = async (title: string, fn: () => Promise<unknown>) => {
    try {
      await fn()
      await onReload()
      shell.toast("success", title)
    } catch (err) {
      shell.toast("error", title, errorMessage(err))
    }
  }

  const download = async (kind: "route_sheet" | "load_list" | "labels") => {
    try {
      await downloadDocument(shell, kind, { route_id: route.id })
    } catch (err) {
      shell.toast("error", "Não foi possível gerar o documento", errorMessage(err))
    }
  }

  return (
    <>
<div className="rounded-lg border p-3">
    <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="text-[13px] font-semibold">
        {vehicles.find((v) => v.id === route.vehicle_id)?.name} · {dayShort(day10(route.day))} · {route.stop_count} paragens
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {actions}
        <AsyncButton size="sm" variant="outline" className="h-7" onRun={() => download("route_sheet")}><FileText className="h-3.5 w-3.5" /> Folha de rota</AsyncButton>
        <AsyncButton size="sm" variant="outline" className="h-7" onRun={() => download("load_list")}><Truck className="h-3.5 w-3.5" /> Lista de carga</AsyncButton>
        <AsyncButton size="sm" variant="outline" className="h-7" onRun={() => download("labels")}><Tags className="h-3.5 w-3.5" /> Etiquetas</AsyncButton>
        {canEdit && (route.status === "confirmed" ? (
          <AsyncButton size="sm" variant="outline" className="h-7" title="Link do portal do motorista para esta rota (F2): entregue com foto ou assinatura, falhou com motivo" onRun={() => shareDriverLink(route.id)}>
            <Smartphone className="h-3.5 w-3.5" /> Link do motorista
          </AsyncButton>
        ) : (
          <Button size="sm" variant="outline" className="h-7" disabled title="O link do motorista fica disponível depois de confirmares o dia (botão «Confirmar» no topo da coluna)">
            <Smartphone className="h-3.5 w-3.5" /> Link do motorista · confirma o dia
          </Button>
        ))}
      </div>
      {driverUrl && driverUrl.route_id === route.id && (
        <div className="flex w-full items-center gap-2 text-[12px]">
          <span className="shrink-0 text-muted-foreground">Motorista:</span>
          <input readOnly className="h-7 min-w-0 flex-1 rounded-md border bg-background px-2 font-mono text-[11px]" value={driverUrl.url ?? ""} onFocus={(e) => e.currentTarget.select()} />
          <span className="shrink-0 text-muted-foreground">{driverUrl.copied ? "copiado" : "seleciona e copia"}</span>
        </div>
      )}
      <span className="w-full text-[12px] text-muted-foreground">
        {n(route.km)?.toLocaleString("pt-PT")} km ({n(route.toll_km)?.toLocaleString("pt-PT")} com portagem) · km {eur(route.cost_km)} · portagens {eur(route.cost_toll)} · hora extra {eur(route.cost_overtime)} · <b>variável {eur(route.cost_variable)}</b> · fixo {eur(route.cost_fixed)}
      </span>
    </div>
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(320px,42%)_1fr]">
      <div className="lg:sticky lg:top-2 lg:self-start">
        <RouteMap shell={shell} routeId={route.id} stops={routeStops} orders={byOrder} warehouse={warehouse} selected={openStop} onSelect={(id) => setOpenStop(id)} />
      </div>
      <div className="flex flex-col divide-y rounded-lg border">
        {routeStops.map((s) => {
          const o = byOrder.get(s.order_id)
          const open = openStop === s.order_id
          return (
            <div key={s.order_id} className={cn("text-[12.5px]", open && "bg-secondary/60")}>
              <button className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left" onClick={() => setOpenStop(open ? null : s.order_id)}>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground font-mono text-[10.5px] text-background">{s.seq}</span>
                <span className="w-11 shrink-0 font-mono">{hhmm(s.arrival_min)}</span>
                <span className="min-w-0 flex-1 truncate">
                  <b>{o?.locality ?? "—"}</b> · {o?.customer_name ?? "—"} <span className="font-mono text-muted-foreground">{o?.source_ref}</span>
                </span>
                {o?.time_window_start && <Tag tone="info">{o.time_window_start}–{o.time_window_end}</Tag>}
                {o?.needs_tail_lift && <Tag tone="info">plataforma</Tag>}
                {s.pinned && <Lock className="h-3 w-3 shrink-0 text-accent" />}
                {s.result === "delivered" && <Tag tone="ok">entregue</Tag>}
                {s.result === "failed" && <Tag tone="bad" title={s.result_reason ?? ""}>falhou</Tag>}
                <span className="w-16 shrink-0 text-right font-mono">{eur(s.fleet_cost)}</span>
              </button>
              {open && o && (
                <div className="flex flex-col gap-1.5 px-2.5 pb-2.5 pl-10">
                  <div>{o.address ?? "—"} · <span className="font-mono">{o.postal_code}</span> {o.customer_phone ? `· ${o.customer_phone}` : ""}</div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <LoadCell o={o} />
                    <span>Prazo <b className="font-mono">{dm(day10(o.due_date))}</b></span>
                    <span>Alternativa {o.delivery_cost != null ? <b className="font-mono">{eur(o.delivery_cost)}</b> : <Tag tone="warn">sem transportadora</Tag>}{o.carrier_deadline ? ` · transportadora até ${dm(day10(o.carrier_deadline))}` : ""}</span>
                  </div>
                  {o.delivery_note && <div className="text-muted-foreground">Nota: «{o.delivery_note}»</div>}
                  {canEdit && route.status === "confirmed" && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {s.result ? (
                        <>
                          <span>{s.result === "delivered" ? "Entregue" : `Falhou: ${s.result_reason ?? ""}`}</span>
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => run("Resultado apagado", () => recordResult(shell, s.order_id, "clear"))}>Apagar resultado</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" className="h-7" onClick={() => run(`${o.source_ref} entregue`, () => recordResult(shell, s.order_id, "delivered"))}>Entregue</Button>
                          <input
                            className="h-7 w-48 rounded-md border bg-background px-2 text-[12.5px]"
                            placeholder="Motivo (ausente, morada…)"
                            value={failReason}
                            onChange={(e) => setFailReason(e.target.value)}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            disabled={!failReason.trim()}
                            onClick={() => run(`${o.source_ref} falhou: volta ao planeamento com prioridade`, async () => {
                              await recordResult(shell, s.order_id, "failed", failReason)
                              setFailReason("")
                            })}
                          >
                            Falhou
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  {canEdit && route.status !== "confirmed" && (
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="h-7" onClick={() => run(s.pinned ? "Encomenda solta" : "Encomenda fixada", () => pinOrder(shell, s.order_id, !s.pinned))}>
                        {s.pinned ? <><LockOpen className="h-3.5 w-3.5" /> Soltar</> : <><Lock className="h-3.5 w-3.5" /> Fixar aqui</>}
                      </Button>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => setMoving(o)}><ArrowRightLeft className="h-3.5 w-3.5" /> Mover…</Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-red-700"
                        title="Tira a encomenda desta rota: volta a «por atribuir» (dá para desfazer)"
                        onClick={() => run(`${o.source_ref} retirada da rota`, () => moveOrder(shell, s.order_id, "unplanned", false))}
                      >
                        <X className="h-3.5 w-3.5" /> Retirar
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
    {route.problems?.length > 0 && (
      <ul className="mt-2 list-disc rounded-md bg-red-50 py-1.5 pl-5 text-[12.5px] text-red-800">
        {route.problems.map((p) => <li key={p}>{p}</li>)}
      </ul>
    )}
    <Note>O custo da frota por encomenda é uma repartição do custo variável da rota (metade por paragem, metade pelo volume); compara-se com a alternativa ao nível da rota e do dia.</Note>
  </div>
      {moving && <MoveDialog shell={shell} order={moving} view={view} vehicles={vehicles} onClose={() => setMoving(null)} onApplied={onReload} />}
    </>
  )
}
